import { MarketplaceCardResponseDto, StepDefinition } from "../../types/types";
import { TemplateStep } from "../../pages/deals/createDeal/dealFormTypes";
import { EVENT_DATE_FIELD_KEY, getVisibleSteps } from "./dealFormStepHelpers";

// Shape of a deal component stored in form data. The backend sends
// paymentTiming / payment_timing / paymentTimingKey which we strip before saving.
export type DealComponent = Record<string, unknown> & {
  paymentTiming?: string;
  payment_timing?: string;
  paymentTimingKey?: string;
};

// Returns a stable step id from a step's stepId or title.
export const getStepId = (step: StepDefinition): string =>
  step.stepId ||
  String(step.title || "step")
    .toLowerCase()
    .replace(/\s+/g, "_");

// Extracts the template steps array from a marketplace card, or returns an empty array.
export const getTemplateSteps = (
  card?: MarketplaceCardResponseDto | null,
): StepDefinition[] =>
  card?.opportunityCardResponse?.templateJson?.steps || [];

// Template steps the person fills in (hidden steps removed). The wizard and the
// validation both use this list so their step indexes match.
export const getVisibleTemplateSteps = (
  card?: MarketplaceCardResponseDto | null,
): TemplateStep[] => getVisibleSteps(getTemplateSteps(card));

// Number of opportunity cards shown per page in the "Make a deal" list.
export const OPPORTUNITY_PAGE_SIZE = 10;

// Reads an event date as a local calendar day. "yyyy-MM-dd" is parsed by hand
// because new Date("yyyy-MM-dd") means UTC midnight, which is the previous day
// in timezones behind UTC. Returns null for missing or invalid values.
const parseEventDate = (value: unknown): Date | null => {
  if (typeof value !== "string" || !value.trim()) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Returns the event date of an opportunity card, or null when it has none.
export const getCardEventDate = (
  card: MarketplaceCardResponseDto,
): Date | null => {
  const formData = card.opportunityCardResponse?.formData;
  if (!formData) return null;

  // The step ids come from the template, so take the first step that has a date.
  const stepWithDate = Object.values(formData).find(
    (stepData) =>
      typeof stepData === "object" &&
      stepData !== null &&
      !Array.isArray(stepData) &&
      (stepData as Record<string, unknown>)[EVENT_DATE_FIELD_KEY],
  ) as Record<string, unknown> | undefined;

  return parseEventDate(stepWithDate?.[EVENT_DATE_FIELD_KEY]);
};

// True for published cards whose event is today or later. Cards without an
// event date stay selectable.
export const isSelectableCard = (
  card: MarketplaceCardResponseDto,
  now: Date = new Date(),
): boolean => {
  const opportunity = card.opportunityCardResponse;
  if (!opportunity?.formData || opportunity.status !== "PUBLISH") return false;

  const eventDate = getCardEventDate(card);
  if (!eventDate) return true;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return eventDate >= today;
};

// Returns how many pages are needed for the given number of items (at least 1).
export const getTotalPages = (itemCount: number, pageSize: number): number =>
  Math.max(1, Math.ceil(itemCount / pageSize));

// Returns the items of one zero-based page.
export const getPageItems = <T>(
  items: T[],
  page: number,
  pageSize: number,
): T[] => items.slice(page * pageSize, (page + 1) * pageSize);

// Normalizes a raw deal type string into a canonical deal type value.
export const normalizeDealType = (
  rawType?: string | null,
): "FIGHT_TOURNAMENT" | "SPARRING" | "FIGHT_SINGLE" => {
  const type = String(rawType || "").toLowerCase();
  if (type.includes("tournament")) return "FIGHT_TOURNAMENT";
  if (type.includes("sparring") || type.includes("training")) return "SPARRING";
  return "FIGHT_SINGLE";
};

// Replaces fighter name fields in saved form data with trimmed values,
// removing old *-Name keys when they differ from the canonical key.
export const normalizeFighterSearchFields = (
  formData: Record<string, any>,
  templateSteps: StepDefinition[],
): Record<string, any> => {
  const result = JSON.parse(JSON.stringify(formData || {}));

  for (const step of templateSteps) {
    const stepData = result[getStepId(step)];
    if (!stepData || typeof stepData !== "object" || Array.isArray(stepData)) {
      continue;
    }

    for (const field of step.fields || []) {
      const isFighterNameField =
        String(field.type || "").toLowerCase() === "fighter_search" &&
        field.storeSelectedAs === "name";
      if (!isFighterNameField) continue;

      const nameKey = field.nameField || field.key;
      const oldNameKey = `${field.key}Name`;
      const savedName =
        stepData[nameKey] ?? stepData[oldNameKey] ?? stepData[field.key];

      if (savedName !== undefined && savedName !== null) {
        stepData[field.key] = String(savedName).trim();
      }
      if (oldNameKey !== field.key) {
        delete stepData[oldNameKey];
      }
    }
  }

  return result;
};

// Removes payment timing fields from each component before saving form data.
export const removeUpfrontPaymentTiming = (
  formData: Record<string, any>,
): Record<string, any> => ({
  ...formData,
  components: (formData.components ?? []).map((component: DealComponent) => {
    const { paymentTiming, payment_timing, paymentTimingKey, ...cleanComponent } =
      component;
    return cleanComponent;
  }),
});

// Builds an array of field descriptors from a plain data object.
export const makeFields = (data: Record<string, unknown>) =>
  Object.keys(data).map((key) => ({
    key,
    label: key,
    type: typeof data[key] === "number" ? "number" : "text",
  }));

// Builds step definitions from saved form data for old deals that have no saved template.
// The `t` parameter is the translation function passed in from the component.
export const buildStepsFromFormData = (
  formData: Record<string, any>,
  t: (key: string) => string,
): TemplateStep[] => {
  const steps: TemplateStep[] = [];

  if (formData.core && Object.keys(formData.core).length > 0) {
    steps.push({
      stepId: "core",
      title: t("coreInformation"),
      fields: makeFields(formData.core),
    });
  }

  if (formData.details && Object.keys(formData.details).length > 0) {
    const detailFields = Object.keys(formData.details)
      .filter((key) => !key.includes("."))
      .map((key) => {
        const value = formData.details[key];
        if (key === "location" && typeof value === "object") {
          return {
            key,
            label: key,
            type: "text",
            fields: Object.keys(value).map((subKey) => ({
              key: subKey,
              label: subKey,
              type: "text",
            })),
          };
        }
        return {
          key,
          label: key,
          type: typeof value === "number" ? "number" : "text",
        };
      });

    steps.push({
      stepId: "details",
      title: t("fightDetails"),
      fields: detailFields,
    });
  }

  if (Array.isArray(formData.components) && formData.components.length > 0) {
    const componentKeys = Object.keys(formData.components[0] || {}).filter(
      (key) => !key.includes("."),
    );

    steps.push({
      stepId: "components",
      title: t("components"),
      repeatable: true,
      fields: componentKeys.map((key) => ({
        key,
        label: key,
        type: key === "value" ? "number" : "text",
      })),
    });
  }

  if (formData.notes && Object.keys(formData.notes).length > 0) {
    steps.push({
      stepId: "notes",
      kind: "notes",
      title: t("additionalNotes"),
      fields: [{ key: "notes", label: "notes", type: "textarea" }],
    });
  }

  const knownKeys = ["core", "details", "components", "notes"];
  for (const [key, value] of Object.entries(formData)) {
    if (knownKeys.includes(key)) continue;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      steps.push({
        stepId: key,
        title: key.charAt(0).toUpperCase() + key.slice(1),
        fields: makeFields(value),
      });
    }
  }

  return steps;
};